/**
 * Route table for ALFIE: public auth screens plus the guarded app shell.
 *
 * Code splitting. Only the landing chat and the auth screens are in the initial
 * bundle — those are the first thing a visitor sees, and a login that has to
 * fetch a chunk before it can render feels broken. Every other screen is a
 * `lazy()` chunk fetched on first navigation, which keeps the entry bundle to
 * the shell and takes the heavy leaves (Valorem's charts, the admin console)
 * off the critical path entirely. `Layout` renders the Suspense boundary, so a
 * chunk load shows a page skeleton inside the shell rather than a blank screen.
 */
import { Suspense, lazy } from 'react';
import type { ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { usePageMeta } from '@/lib/seo';
import { RequireAuth, RequireRole } from '@/components/ProtectedRoute';
import { PageSkeleton } from '@/components/PageSkeleton';

/**
 * `lazy()` for a module with named exports. Every page in ALFIE is exported by
 * name (never default), so the promise is mapped to the shape `lazy` expects.
 */
function lazyPage<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M & string) {
  return lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));
}

// ------------------------------------------------------------ eager chunks
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import { ContactPage } from '@/pages/ContactPage';
import { ChatPage } from '@/pages/ChatPage';

// ------------------------------------------------------------- lazy chunks
const SystemStatusPage = lazyPage(() => import('@/pages/SystemStatusPage'), 'SystemStatusPage');
const DashboardPage = lazyPage(() => import('@/pages/DashboardPage'), 'DashboardPage');
const DocGeneratorPage = lazyPage(() => import('@/pages/DocGeneratorPage'), 'DocGeneratorPage');
const DocumentPage = lazyPage(() => import('@/pages/DocumentPage'), 'DocumentPage');
const DocIntakePage = lazyPage(() => import('@/pages/docgen/DocIntakePage'), 'DocIntakePage');
const DeconstructorPage = lazyPage(() => import('@/pages/DeconstructorPage'), 'DeconstructorPage');
const ResearchPage = lazyPage(() => import('@/pages/ResearchPage'), 'ResearchPage');
const DrafterPage = lazyPage(() => import('@/pages/DrafterPage'), 'DrafterPage');
const SummarizerPage = lazyPage(() => import('@/pages/SummarizerPage'), 'SummarizerPage');
const AnalyzerPage = lazyPage(() => import('@/pages/AnalyzerPage'), 'AnalyzerPage');
const HeadnotePage = lazyPage(() => import('@/pages/HeadnotePage'), 'HeadnotePage');
const PrecedentPage = lazyPage(() => import('@/pages/PrecedentPage'), 'PrecedentPage');
const CitatorPage = lazyPage(() => import('@/pages/CitatorPage'), 'CitatorPage');
const SourcePage = lazyPage(() => import('@/pages/SourcePage'), 'SourcePage');
const BriefAuditorPage = lazyPage(() => import('@/pages/BriefAuditorPage'), 'BriefAuditorPage');
const CiteCheckPage = lazyPage(() => import('@/pages/CiteCheckPage'), 'CiteCheckPage');
const RedlinePage = lazyPage(() => import('@/pages/RedlinePage'), 'RedlinePage');
const DeadlinesPage = lazyPage(() => import('@/pages/DeadlinesPage'), 'DeadlinesPage');
const ChronologyPage = lazyPage(() => import('@/pages/ChronologyPage'), 'ChronologyPage');
const RedactionPage = lazyPage(() => import('@/pages/RedactionPage'), 'RedactionPage');
const GazettePage = lazyPage(() => import('@/pages/GazettePage'), 'GazettePage');
const GazetteIssuePage = lazyPage(() => import('@/pages/gazette/GazetteIssuePage'), 'GazetteIssuePage');
const PublicationEditorPage = lazyPage(() => import('@/pages/gazette/PublicationEditorPage'), 'PublicationEditorPage');
const ColloquyPage = lazyPage(() => import('@/pages/ColloquyPage'), 'ColloquyPage');
const ValoremListPage = lazyPage(() => import('@/pages/valorem/ValoremListPage'), 'ValoremListPage');
const ValoremProfilePage = lazyPage(() => import('@/pages/valorem/ValoremProfilePage'), 'ValoremProfilePage');
const ValoremRunPage = lazyPage(() => import('@/pages/valorem/ValoremRunPage'), 'ValoremRunPage');
const FactsPage = lazyPage(() => import('@/pages/FactsPage'), 'FactsPage');
const MattersPage = lazyPage(() => import('@/pages/MattersPage'), 'MattersPage');
const MatterDetailPage = lazyPage(() => import('@/pages/MatterDetailPage'), 'MatterDetailPage');
const WatchesPage = lazyPage(() => import('@/pages/WatchesPage'), 'WatchesPage');
const AlertPage = lazyPage(() => import('@/pages/AlertPage'), 'AlertPage');
const FirmBrainPage = lazyPage(() => import('@/pages/FirmBrainPage'), 'FirmBrainPage');
const OrgsPage = lazyPage(() => import('@/pages/OrgsPage'), 'OrgsPage');
const EnginesPage = lazyPage(() => import('@/pages/EnginesPage'), 'EnginesPage');
const SettingsPage = lazyPage(() => import('@/pages/SettingsPage'), 'SettingsPage');
const AdminUsersPage = lazyPage(() => import('@/pages/admin/AdminUsersPage'), 'AdminUsersPage');
const AdminAuditPage = lazyPage(() => import('@/pages/admin/AdminAuditPage'), 'AdminAuditPage');
const AdminAiPage = lazyPage(() => import('@/pages/admin/AdminAiPage'), 'AdminAiPage');
const AdminLibraryPage = lazyPage(() => import('@/pages/admin/AdminLibraryPage'), 'AdminLibraryPage');
const AdminLayersPage = lazyPage(() => import('@/pages/admin/AdminLayersPage'), 'AdminLayersPage');
const AdminContentPage = lazyPage(() => import('@/pages/admin/AdminContentPage'), 'AdminContentPage');
const AdminIngestionPage = lazyPage(() => import('@/pages/admin/AdminIngestionPage'), 'AdminIngestionPage');
const AdminConnectorsPage = lazyPage(() => import('@/pages/admin/AdminConnectorsPage'), 'AdminConnectorsPage');
const AdminValoremPage = lazyPage(() => import('@/pages/admin/AdminValoremPage'), 'AdminValoremPage');
const AdminDeadlinePacksPage = lazyPage(() => import('@/pages/admin/AdminDeadlinePacksPage'), 'AdminDeadlinePacksPage');

export function App() {
  usePageMeta();   // per-route <title> + meta description (SEO / tab labels)
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      <Route element={<Layout />}>
        {/* Public chatbot home — usable by anonymous and registered users. */}
        <Route path="/" element={<ChatPage />} />
        <Route path="/chat" element={<Navigate to="/" replace />} />
        {/* A specific conversation — linkable, survives reload, same public page. */}
        <Route path="/chat/:conversationId" element={<ChatPage />} />
        {/* Public contact form — no account needed. */}
        <Route path="/contact" element={<ContactPage />} />
        {/* Diagnostics. Public on purpose: "is it me or you?" must be
            answerable before anyone can sign in. */}
        <Route path="/status" element={<SystemStatusPage />} />

        {/* Everything below requires a signed-in user. */}
        <Route element={<RequireAuth />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/documents" element={<DocGeneratorPage />} />
          {/* An intake in progress — resumable by URL. `/documents/new` alone is not a document. */}
          <Route path="/documents/new" element={<Navigate to="/documents" replace />} />
          <Route path="/documents/new/:submissionId" element={<DocIntakePage />} />
          <Route path="/documents/:documentId" element={<DocumentPage />} />
          <Route path="/drafter" element={<DrafterPage />} />
          <Route path="/drafter/:sessionId" element={<DrafterPage />} />
          <Route path="/deconstructor" element={<DeconstructorPage />} />
          <Route path="/deconstructor/:deconstructionId" element={<DeconstructorPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/research/:runId" element={<ResearchPage />} />
          <Route path="/summarizer" element={<SummarizerPage />} />
          <Route path="/analyzer" element={<AnalyzerPage />} />
          <Route path="/analyzer/:analysisId" element={<AnalyzerPage />} />
          <Route path="/headnotes" element={<HeadnotePage />} />
          <Route path="/precedent" element={<PrecedentPage />} />
          <Route path="/citator" element={<CitatorPage />} />
          <Route path="/sources/:sourceId" element={<SourcePage />} />
          <Route path="/brief-audit" element={<BriefAuditorPage />} />
          <Route path="/cite-check" element={<CiteCheckPage />} />
          <Route path="/cite-check/:documentId" element={<CiteCheckPage />} />
          <Route path="/redline" element={<RedlinePage />} />
          <Route path="/redline/:redlineId" element={<RedlinePage />} />
          <Route path="/deadlines" element={<DeadlinesPage />} />
          <Route path="/chronology" element={<ChronologyPage />} />
          <Route path="/redaction" element={<RedactionPage />} />
          <Route path="/gazette" element={<GazettePage />} />
          {/* A saved publication's editor; `/gazette/publications` alone is the list tab, not an issue id. */}
          <Route path="/gazette/publications" element={<Navigate to="/gazette?tab=publications" replace />} />
          <Route path="/gazette/publications/new" element={<PublicationEditorPage />} />
          <Route path="/gazette/publications/:publicationId" element={<PublicationEditorPage />} />
          <Route path="/gazette/:issueId" element={<GazetteIssuePage />} />
          <Route path="/colloquy" element={<ColloquyPage />} />
          <Route path="/colloquy/:reviewId" element={<ColloquyPage />} />
          <Route path="/valorem" element={<ValoremListPage />} />
          <Route path="/valorem/runs/:runId" element={<ValoremRunPage />} />
          <Route path="/valorem/:profileId" element={<ValoremProfilePage />} />
          <Route path="/facts" element={<FactsPage />} />
          <Route path="/matters" element={<MattersPage />} />
          <Route path="/matters/:id" element={<MatterDetailPage />} />
          <Route path="/watches" element={<WatchesPage />} />
          <Route path="/watches/alerts/:notificationId" element={<AlertPage />} />
          <Route path="/firm-brain" element={<FirmBrainPage />} />
          <Route path="/orgs" element={<OrgsPage />} />
          <Route path="/settings" element={<SettingsPage />} />

          {/* Staff (ADMIN or DEV) — AI engine/model configuration is admin-only. */}
          <Route element={<RequireRole anyOf={['ADMIN', 'DEV']} />}>
            <Route path="/engines" element={<EnginesPage />} />
            <Route path="/admin/ai" element={<AdminAiPage />} />
            <Route path="/admin/library" element={<AdminLibraryPage />} />
            <Route path="/admin/layers" element={<AdminLayersPage />} />
            <Route path="/admin/content" element={<AdminContentPage />} />
            <Route path="/admin/ingestion" element={<AdminIngestionPage />} />
            <Route path="/admin/connectors" element={<AdminConnectorsPage />} />
            <Route path="/admin/valorem" element={<AdminValoremPage />} />
            <Route path="/admin/deadline-packs" element={<AdminDeadlinePacksPage />} />
          </Route>

          {/* Admin only */}
          <Route element={<RequireRole anyOf={['ADMIN']} />}>
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/audit" element={<AdminAuditPage />} />
          </Route>
        </Route>
      </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
